import type {
  BundleEntry,
  CodeableConcept,
  Patient,
  Reference,
  Resource,
  Task,
} from '@medplum/fhirtypes'
import { groupBy } from 'lodash-es'
import { match } from 'ts-pattern'
import { z } from 'zod'
import { codeableConcept, hasCodeableConcept } from '#src/codeableConcept.ts'
import { TASK_CATEGORIES } from '#src/models/tasks/constants.ts'
import { ExtendedTask } from '#src/models/tasks/task.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, now } from '#src/utils.ts'

/**
 * Subset of the Task intent and status values that are supported by the enrollment task
 */
const SUPPORTED_TASK_INTENT = {
  PROPOSAL: 'proposal',
  PLAN: 'plan',
  ORDER: 'order',
} satisfies Record<string, Task['intent']>

const SUPPORTED_TASK_STATUS = {
  RECEIVED: 'received',
  ACCEPTED: 'accepted',
  IN_PROGRESS: 'in-progress',
  READY: 'ready',
  COMPLETED: 'completed',

  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  ON_HOLD: 'on-hold',
  FAILED: 'failed',
} satisfies Record<string, Task['status']>

export type EnrollmentState =
  | 'InitialLead'
  | 'QualifiedLead'
  | 'LocPreparation'
  | 'LocSubmitted'
  | 'LocApproved'
  | 'ParticipantApproved'
  | 'EnrolledParticipant'
  | 'Rejected'
  | 'LocDenied'

export class EnrollmentTask extends ExtendedTask<EnrollmentState> {
  constructor(data?: Partial<Omit<Task, 'resourceType'>>) {
    super({
      priority: 'routine',
      code: TASK_CATEGORIES.ENROLLMENT_TASK,
      ...STATE.InitialLead,
      ...data,
    })
  }

  static stateSchema = z.enum([
    'InitialLead',
    'QualifiedLead',
    'LocPreparation',
    'LocSubmitted',
    'LocApproved',
    'ParticipantApproved',
    'EnrolledParticipant',
    'Rejected',
    'LocDenied',
  ])

  override mapStateToTaskFields(state: EnrollmentState): {
    businessStatus: CodeableConcept
    intent: Task['intent']
    status: Task['status']
  } {
    return STATE[state]
  }

  get state(): EnrollmentState {
    return super.state
  }

  set state(state: EnrollmentState) {
    super.state = state
    this.startDate = now()
  }

  static searchQuery(): QueryTypes {
    return {
      code: TASK_CATEGORIES.ENROLLMENT_TASK,
      'status:not': 'entered-in-error',
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  static enrollmentTasksTableQuery(): QueryTypes {
    return {
      ...this.searchQuery(),
      'business-status:not': 'EnrolledParticipant',
    }
  }

  static byState(state: EnrollmentState): QueryTypes {
    return {
      ...this.searchQuery(),
      'business-status': STATE[state].businessStatus,
      intent: STATE[state].intent,
      status: STATE[state].status,
    }
  }

  static byPatient(patient: Reference<Patient>): QueryTypes {
    return {
      ...this.searchQuery(),
      subject: patient,
    }
  }

  static isInstance(task: Resource): task is Task {
    return (
      isOfType(task, 'Task') &&
      hasCodeableConcept(task.code, TASK_CATEGORIES.ENROLLMENT_TASK)
    )
  }

  static getStageToCompletionDate(
    taskVersions: BundleEntry<Task>[]
  ): Map<EnrollmentState, string | undefined> {
    const stageToCompletionDate = new Map<EnrollmentState, string | undefined>()
    const tasks = taskVersions.flatMap(({ resource }) =>
      resource === undefined ? [] : [EnrollmentTask.fromFhirResource(resource)]
    )
    const tasksByState = groupBy(tasks, (task) => task.state)
    for (const [state, tasks] of Object.entries(tasksByState)) {
      const latestCompletionDate = tasks
        .map((task) => task.lastUpdated)
        .filter((x) => x !== undefined)
        .sort()
        .at(-1)
      if (!latestCompletionDate) {
        continue
      }
      stageToCompletionDate.set(state as EnrollmentState, latestCompletionDate)
    }
    return stageToCompletionDate
  }

  get stateOrder(): number {
    return match(this.state)
      .with('InitialLead', () => 0)
      .with('QualifiedLead', () => 1)
      .with('LocPreparation', () => 2)
      .with('LocSubmitted', () => 3)
      .with('LocApproved', () => 4)
      .with('ParticipantApproved', () => 5)
      .with('EnrolledParticipant', () => 6)
      .with('Rejected', () => 7)
      .with('LocDenied', () => 8)
      .exhaustive()
  }

  static stateText(state: EnrollmentState): string {
    return match(state)
      .with('InitialLead', () => '1. Initial Lead')
      .with('QualifiedLead', () => '2. Eligibility')
      .with('LocPreparation', () => '3. LOC Preparation')
      .with('LocSubmitted', () => '4. DHCS Review')
      .with('LocApproved', () => '5. LOC Approved')
      .with('ParticipantApproved', () => '6. Participant Approved')
      .with('EnrolledParticipant', () => '7. Enrolled')
      .with('Rejected', () => 'Rejected')
      .with('LocDenied', () => 'LOC Denied')
      .exhaustive()
  }

  get stateText(): string {
    return EnrollmentTask.stateText(this.state)
  }
}

const BUSINESS_STATUS = {
  [EnrollmentTask.stateSchema.enum.InitialLead]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'InitialLead',
    display: '1. Initial Lead',
  }),
  [EnrollmentTask.stateSchema.enum.QualifiedLead]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'QualifiedLead',
    display: '2. Eligibility',
  }),
  [EnrollmentTask.stateSchema.enum.LocPreparation]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'LocPreparation',
    display: '3. LOC Preparation',
  }),
  [EnrollmentTask.stateSchema.enum.LocSubmitted]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'LocSubmitted',
    display: '4. DHCS Review',
  }),
  [EnrollmentTask.stateSchema.enum.LocApproved]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'LocApproved',
    display: '5. LOC Approved',
  }),
  [EnrollmentTask.stateSchema.enum.ParticipantApproved]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'ParticipantApproved',
    display: '6. Participant Approved',
  }),
  [EnrollmentTask.stateSchema.enum.EnrolledParticipant]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'EnrolledParticipant',
    display: '7. Enrolled',
  }),
  [EnrollmentTask.stateSchema.enum.Rejected]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'Rejected',
    display: 'Rejected',
  }),
  [EnrollmentTask.stateSchema.enum.LocDenied]: codeableConcept({
    system: 'https://www.seenhealth.org/CodeSystem/enrollment-intake-stage',
    code: 'LocDenied',
    display: 'LOC Denied',
  }),
} satisfies Record<EnrollmentState, CodeableConcept>

const STATE = {
  [EnrollmentTask.stateSchema.enum.InitialLead]: {
    businessStatus: BUSINESS_STATUS.InitialLead,
    intent: SUPPORTED_TASK_INTENT.PROPOSAL,
    status: SUPPORTED_TASK_STATUS.RECEIVED,
  },
  [EnrollmentTask.stateSchema.enum.QualifiedLead]: {
    businessStatus: BUSINESS_STATUS.QualifiedLead,
    intent: SUPPORTED_TASK_INTENT.PLAN,
    status: SUPPORTED_TASK_STATUS.ACCEPTED,
  },
  [EnrollmentTask.stateSchema.enum.LocPreparation]: {
    businessStatus: BUSINESS_STATUS.LocPreparation,
    intent: SUPPORTED_TASK_INTENT.PLAN,
    status: SUPPORTED_TASK_STATUS.IN_PROGRESS,
  },
  [EnrollmentTask.stateSchema.enum.LocSubmitted]: {
    businessStatus: BUSINESS_STATUS.LocSubmitted,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.IN_PROGRESS,
  },
  [EnrollmentTask.stateSchema.enum.LocApproved]: {
    businessStatus: BUSINESS_STATUS.LocApproved,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.IN_PROGRESS,
  },
  [EnrollmentTask.stateSchema.enum.ParticipantApproved]: {
    businessStatus: BUSINESS_STATUS.ParticipantApproved,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.IN_PROGRESS,
  },
  [EnrollmentTask.stateSchema.enum.EnrolledParticipant]: {
    businessStatus: BUSINESS_STATUS.EnrolledParticipant,
    intent: SUPPORTED_TASK_INTENT.ORDER,
    status: SUPPORTED_TASK_STATUS.COMPLETED,
  },
  [EnrollmentTask.stateSchema.enum.Rejected]: {
    businessStatus: BUSINESS_STATUS.Rejected,
    intent: SUPPORTED_TASK_INTENT.PROPOSAL,
    status: SUPPORTED_TASK_STATUS.REJECTED,
  },
  [EnrollmentTask.stateSchema.enum.LocDenied]: {
    businessStatus: BUSINESS_STATUS.LocDenied,
    intent: SUPPORTED_TASK_INTENT.PLAN,
    status: SUPPORTED_TASK_STATUS.FAILED,
  },
} satisfies Record<
  keyof typeof BUSINESS_STATUS,
  {
    businessStatus: CodeableConcept
    intent: Task['intent']
    status: Task['status']
  }
>
